import type { IPickPageRemoveConfirmState } from '../types';

import { useRemoveTemplateFromCampaign } from '~/modules/asset-template';
import { ICampaignType } from '~/modules/campaign/types';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IUsePickPageRemoveConfirmParams {
  /** Usage is read from the API, so a removal only shows up once it is refetched. */
  handleRefetchUsage: () => Promise<void>;
}

export interface IUsePickPageRemoveConfirmResult {
  /** Single instance shared by every row — a row only opens it, the modal renders it. */
  removeConfirmState: IPickPageRemoveConfirmState | null;
  removeConfirmDescription: string;
  isRemovingTemplate: boolean;
  handleOpenRemoveConfirm: (params: IPickPageRemoveConfirmState) => void;
  handleCloseRemoveConfirm: () => void;
  handleConfirmRemove: () => Promise<void>;
}

/** Hands a page back from the other draft experiment locking it, straight from the picker. */
export const usePickPageRemoveConfirm = ({
  handleRefetchUsage,
}: IUsePickPageRemoveConfirmParams): IUsePickPageRemoveConfirmResult => {
  const { t } = useTranslation();
  const [removeConfirmState, setRemoveConfirmState] = useState<IPickPageRemoveConfirmState | null>(
    null,
  );

  const { handleRemoveTemplate, isLoading: isRemovingTemplate } = useRemoveTemplateFromCampaign();

  const handleOpenRemoveConfirm = useCallback((params: IPickPageRemoveConfirmState) => {
    setRemoveConfirmState(params);
  }, []);

  const handleCloseRemoveConfirm = useCallback(() => {
    if (isRemovingTemplate) return;
    setRemoveConfirmState(null);
  }, [isRemovingTemplate]);

  const handleConfirmRemove = useCallback(async () => {
    if (!removeConfirmState) return;

    // Usage here is read from `campaignVariantTargets`, not the campaign stores this session
    // loaded once, so the removal has to work off the API too or it would silently do nothing.
    const isRemoved = await handleRemoveTemplate({
      ...removeConfirmState,
      shouldFetchLatest: true,
    });
    if (!isRemoved) return;

    await handleRefetchUsage();
    setRemoveConfirmState(null);
  }, [removeConfirmState, handleRemoveTemplate, handleRefetchUsage]);

  const removeConfirmDescription = useMemo(() => {
    const name = removeConfirmState?.campaignName;

    switch (removeConfirmState?.campaignType) {
      case ICampaignType.PRICE:
        return t('Are you sure you want to remove product from "{{name}}"?', { name });

      // A URL experiment holds the page as a destination, so what it gives back is the URL itself.
      case ICampaignType.URL:
        return t('Are you sure you want to remove URL from "{{name}}"?', { name });

      // A template experiment gives back the template the product renders, not the product itself.
      case ICampaignType.TEMPLATE:
      case ICampaignType.TEMPLATE_BUNDLE:
      default:
        return t('Are you sure you want to remove template from "{{name}}"?', { name });
    }
  }, [t, removeConfirmState]);

  return {
    removeConfirmState,
    removeConfirmDescription,
    isRemovingTemplate,
    handleOpenRemoveConfirm,
    handleCloseRemoveConfirm,
    handleConfirmRemove,
  };
};
